"use client";

import { Edit, ExternalLink, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  SOCIAL_TYPE_CONFIG,
  SocialLinks,
  SocialType,
} from "../../../../../data/venue_pages";

interface SocialIconProps {
  icon: any;
  size?: number;
  isConnected?: boolean;
  displayMode?: "color" | "grayscale";
}

const SocialIcon: React.FC<SocialIconProps> = ({
  icon,
  size = 48,
  isConnected = false,
  displayMode,
}) => {
  const isGrayscale = displayMode === "grayscale";
  const defaultGray = displayMode === undefined && !isConnected;
  const common = { filter: defaultGray ? "grayscale(100%)" : "none" };

  if (icon.svg && icon.hex) {
    return (
      <span
        style={{
          display: "inline-block",
          width: size,
          height: size,
          fill: isGrayscale ? "#A4A6A8" : `#${icon.hex}`,
          ...common,
        }}
        dangerouslySetInnerHTML={{ __html: icon.svg }}
      />
    );
  }
  const IconComponent = icon;
  return (
    <IconComponent
      size={size}
      style={{ color: isGrayscale ? "#A4A6A8" : undefined, ...common }}
    />
  );
};

interface ConnectedSocialLinksProps {
  venueId: string;
  socialLinks: SocialLinks;
  onEdit?: () => void;
  displayMode?: "color" | "grayscale";
  cardClassName?: string;
  iconSize?: number;
  cardSize?: number;
  cardPadding?: number;
  publicDisplay?: boolean;
  title?: string;
  border?: boolean;
  layout?: "grid" | "horizontal";
}

const ConnectedSocialLinks: React.FC<ConnectedSocialLinksProps> = ({
  socialLinks,
  onEdit,
  displayMode,
  cardClassName,
  iconSize,
  cardSize,
  cardPadding,
  publicDisplay = false,
  title,
  border = true,
  layout = "grid",
}) => {
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    url: string;
    key: SocialType;
    isConnected: boolean;
  } | null>(null);

  useEffect(() => {
    if (!publicDisplay) {
      const handleClick = () => contextMenu && setContextMenu(null);
      window.addEventListener("click", handleClick);
      return () => window.removeEventListener("click", handleClick);
    }
  }, [contextMenu, publicDisplay]);

  const handleContextMenu = (
    e: React.MouseEvent,
    key: SocialType,
    url?: string
  ) => {
    if (!publicDisplay) {
      e.preventDefault();
      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        url: url || "",
        key,
        isConnected: !!url,
      });
    }
  };

  const socialTypes = Object.values(SocialType) as SocialType[];
  const filteredSocial = publicDisplay
    ? socialTypes.filter((s) => socialLinks[s])
    : socialTypes;
  const mobileCols =
    filteredSocial.length > 4
      ? Math.ceil(filteredSocial.length / 2)
      : filteredSocial.length;
  const desktopCols = filteredSocial.length >= 5 ? 5 : filteredSocial.length;

  const cards = filteredSocial.map((social) => {
    const url = socialLinks[social];
    const icon = SOCIAL_TYPE_CONFIG[social].icon;
    const classes = cardSize
      ? `flex items-center justify-center ${cardClassName || ""}`
      : `${
          publicDisplay
            ? "flex flex-col items-center justify-center"
            : "flex flex-col items-center justify-center p-3 border rounded shadow-sm transition duration-200 hover:shadow-md"
        } ${cardClassName || ""} ${url ? "opacity-100" : "opacity-30"}`;
    const style = cardSize
      ? {
          width: `${cardSize}px`,
          height: `${cardSize}px`,
          padding: cardPadding !== undefined ? `${cardPadding}px` : undefined,
          opacity: url ? 1 : 0.3,
        }
      : undefined;
    const cardContent = (
      <div className={classes} style={style}>
        <SocialIcon
          icon={icon}
          size={iconSize || 48}
          isConnected={!!url}
          displayMode={displayMode}
        />
      </div>
    );
    if (publicDisplay)
      return (
        <div key={social} className="relative">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            {cardContent}
          </a>
        </div>
      );
    return (
      <div key={social} className="relative group">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            onContextMenu={(e) => handleContextMenu(e, social, url)}
          >
            {cardContent}
          </a>
        ) : (
          <div
            className="cursor-pointer"
            onClick={onEdit}
            onContextMenu={(e) => handleContextMenu(e, social)}
          >
            {cardContent}
          </div>
        )}
        {!url && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-black bg-opacity-30 opacity-0 rounded group-hover:opacity-100 transition-opacity duration-200">
            <Plus className="w-6 h-6 text-white" />
          </div>
        )}
      </div>
    );
  });

  const inner = (
    <>
      {layout === "horizontal" ? (
        <div className="flex gap-2 overflow-x-auto flex-wrap md:flex-nowrap">
          {cards}
        </div>
      ) : (
        <>
          <div className="block md:hidden">
            <div className="flex flex-row flex-wrap gap-x-4 gap-y-3">
              {cards}
            </div>
          </div>
          <div className="hidden md:block">
            <div className="flex flex-row flex-wrap gap-x-4 gap-y-3">
              {cards}
            </div>
          </div>
        </>
      )}
      {!publicDisplay && contextMenu?.visible && (
        <div
          className="absolute z-50 bg-white border rounded shadow-md py-2"
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            width: 120,
          }}
        >
          {contextMenu.isConnected ? (
            <>
              <div
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                onClick={() => {
                  window.open(contextMenu.url, "_blank", "noopener,noreferrer");
                  setContextMenu(null);
                }}
              >
                <ExternalLink size={16} />
                <span className="ml-2">Visit</span>
              </div>
              <div
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                onClick={() => {
                  setContextMenu(null);
                  onEdit && onEdit();
                }}
              >
                <Edit size={16} />
                <span className="ml-2">Edit</span>
              </div>
            </>
          ) : (
            <div
              className="px-2 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
              onClick={() => {
                setContextMenu(null);
                onEdit && onEdit();
              }}
            >
              <Plus size={16} />
              <span className="ml-2">Add Link</span>
            </div>
          )}
        </div>
      )}
    </>
  );

  return border ? (
    <div className="flex flex-col gap-y-4 w-full rounded-xl border border-text-secondary p-6 bg-white">
      {title && <p className="text-xl text-primary-blue font-bold ">{title}</p>}
      <div className="relative overflow-x-auto">{inner}</div>
    </div>
  ) : (
    inner
  );
};

export default ConnectedSocialLinks;
